<script setup>
import BalanceSheet from "@/sections/dashboard/BalanceSheet.vue";
import FinanceSnapshot from "@/sections/dashboard/FinanceSnapshot.vue";
import FinancialInformationWizard from "@/components/dashboard/FinancialInformationWizard.vue";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";

const store = useUserStore();
const showWizard = ref(false);

// Check if financial statement is empty/null
const hasFinancialData = computed(() => {
  const fs = store.financialStatement;
  if (!fs) return false;

  // Check if key financial data exists
  const hasIncome = fs.customerIncome && fs.customerIncome.length > 0;
  const hasExpenses = fs.customerExpenses && fs.customerExpenses.length > 0;
  const hasLiabilities = fs.customerLiabilities && fs.customerLiabilities.length > 0;

  return hasIncome || hasExpenses || hasLiabilities;
});

const openWizard = () => {
  showWizard.value = true;
};
</script>

<template>
  <div class="financial-statements">
    <!-- CTA Banner for Missing Financial Information -->
    <div v-if="!hasFinancialData" class="financial-cta-banner">
      <div class="cta-icon">
        <i class="pi pi-info-circle"></i>
      </div>
      <div class="cta-content">
        <h3 class="cta-title">Complete Your Financial Profile</h3>
        <p class="cta-description">
          As your SEBI-registered Investment Adviser, we need comprehensive insights into your financial situation
          to provide personalized, compliant investment recommendations. Your income, expenses, savings, and insurance
          details enable us to craft strategies aligned with your unique circumstances and goals.
        </p>
        <button class="cta-button" @click="openWizard">
          <i class="pi pi-pencil"></i>
          Provide Financial Information
        </button>
      </div>
    </div>

    <section>
      <FinanceSnapshot />
    </section>
    <section>
      <BalanceSheet />
    </section>

    <!-- Financial Information Wizard Dialog -->
    <FinancialInformationWizard v-model="showWizard" />
  </div>
</template>

<style scoped>
.financial-statements {
  padding: 20px;
}

/* CTA Banner Styles */
.financial-cta-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.1);
}

.cta-icon {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.cta-icon i {
  font-size: 1.5rem;
  color: white;
}

.cta-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cta-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
}

.cta-description {
  font-size: 0.95rem;
  color: #1e40af;
  line-height: 1.5;
  margin: 0;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--an-primary, #0d7c68);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
}

.cta-button:hover {
  background: var(--an-primary-dark, #0a6454);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 124, 104, 0.4);
}

.cta-button:active {
  transform: translateY(0);
}

.cta-button i {
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .financial-cta-banner {
    flex-direction: column;
    padding: 20px;
    gap: 16px;
  }

  .cta-icon {
    width: 50px;
    height: 50px;
    align-self: center;
  }

  .cta-icon i {
    font-size: 1.5rem;
  }

  .cta-title {
    font-size: 1.2rem;
    text-align: center;
  }

  .cta-description {
    font-size: 0.9rem;
    text-align: center;
  }

  .cta-button {
    align-self: stretch;
    justify-content: center;
  }
}
</style>
